import React, { useEffect, useState, useRef } from 'react';
import { flushSync } from 'react-dom';
import {
  Typography, Button, Box, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, CircularProgress, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TableSortLabel, Stack, Tabs, Tab, Slider, Accordion, AccordionSummary, AccordionDetails, Menu
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Checkbox from '@mui/material/Checkbox';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { getSkis, addSki, updateSki, deleteSki, getTests, getRaces, regenerateSkiQr, updateSkiOrder } from '../api';
import { decodeFischerQr } from '../utils/fischerQrDecoder';
import { TestDetailModal } from './TestsSection';
import InfoIcon from '@mui/icons-material/Info';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { exportSkisToPDF } from '../utils/pdfExport';
import { useAuth } from '../AuthContext';
import QRCodeDisplay from '../components/QRCodeDisplay';
import { printAllSkiQrLabels, printSingleSkiQrLabels } from '../utils/printQrLabels';
import SkiSpreadsheetUpload from '../components/SkiSpreadsheetUpload';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import TransferOwnershipDialog from '../components/TransferOwnershipDialog';
import SkiTransferNotifications from '../components/SkiTransferNotifications';
import SentSkiTransfers from '../components/SentSkiTransfers';

const BRANDS = [
  'Fischer',
  'Salomon',
  'Atomic',
  'Rossignol',
  'Madshus',
];

export function SkiForm({ open, onClose, onSave, initial, defaultTechnique }) {
  const [form, setForm] = useState(initial || {
    ski_number: '', serial_number: '', brand: '', fa: '', hr: '', mf: '', h4: '', grind: '', mold: '', base: '', ski_length_cm:'', year_made: '', type: defaultTechnique || 'classic', rossi_flex: '', rossi_height: '', rossi_stiffness: '', temperature_color: null, ski_category: '', notes: ''
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [showQrScanner, setShowQrScanner] = useState(false);
  const [stream, setStream] = useState(null);
  // Use refs to track interval and timeout to prevent resource leaks
  const scanningIntervalRef = useRef(null);
  const scanningTimeoutRef = useRef(null);
  const isScanningRef = useRef(false);
  
  useEffect(() => {
    setForm(initial || {
      ski_number: '', serial_number: '', brand: '', fa: '', hr: '', mf: '', h4: '', grind: '', mold: '', base: '', ski_length_cm:'', year_made: '', type: defaultTechnique || 'classic', rossi_flex: '', rossi_height: '', rossi_stiffness: '', temperature_color: null, ski_category: '', notes: ''
    });
    setError('');
    setShowQrScanner(false);
    // Clean up any pending timeouts/intervals when form closes
    if (!open) {
      isScanningRef.current = false;
      if (scanningTimeoutRef.current) {
        clearTimeout(scanningTimeoutRef.current);
        scanningTimeoutRef.current = null;
      }
      if (scanningIntervalRef.current) {
        clearInterval(scanningIntervalRef.current);
        scanningIntervalRef.current = null;
      }
    }
  }, [open, initial, defaultTechnique]);

  const handleChange = e => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleQrScanned = (qrData) => {
    const decoded = decodeFischerQr(qrData);
    if (decoded) {
      // Extract last 3 digits of serial number for ski number
      const skiNumber = decoded.serial_number 
        ? decoded.serial_number.slice(-3) 
        : null;
      
      // Pre-fill form with decoded data
      setForm(f => ({
        ...f,
        brand: 'Fischer',
        ski_number: skiNumber || f.ski_number,
        serial_number: decoded.serial_number || f.serial_number,
        fa: decoded.fa || f.fa,
        hr: decoded.hr || f.hr,
        ski_length_cm: decoded.ski_length_cm || f.ski_length_cm,
        year_made: decoded.year_made || f.year_made,
        type: decoded.type || f.type || defaultTechnique || 'classic'
      }));
      handleStopScan();
      setError('');
    } else {
      setError('Could not decode QR code. Please try again or enter information manually.');
    }
  };

  const handleStopScan = async () => {
    isScanningRef.current = false;
    
    // Clear scanning timeout if it hasn't executed yet
    if (scanningTimeoutRef.current) {
      clearTimeout(scanningTimeoutRef.current);
      scanningTimeoutRef.current = null;
    }
    
    // Clear scanning interval
    if (scanningIntervalRef.current) {
      clearInterval(scanningIntervalRef.current);
      scanningIntervalRef.current = null;
    }
    
    // Stop video stream
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    
    setShowQrScanner(false);
  };

  const handleStartScan = async () => {
    try {
      isScanningRef.current = true;
      
      // Check if BarcodeDetector is supported
      if (!('BarcodeDetector' in window)) {
        setError('QR code scanning is not supported in this browser. Please use Chrome, Edge, or another modern browser.');
        isScanningRef.current = false;
        return;
      }

      // Request camera access with better mobile support
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      
      // Check if scanning was stopped while waiting for camera
      if (!isScanningRef.current) {
        mediaStream.getTracks().forEach(track => track.stop());
        return;
      }
      
      setStream(mediaStream);
      
      // Wait for video element to be ready - longer timeout for mobile
      // Store timeout ID in ref so it can be cleared if stop is called early
      scanningTimeoutRef.current = setTimeout(() => {
        scanningTimeoutRef.current = null; // Clear ref after timeout executes
        
        // Check if scanning was stopped before timeout completed
        if (!isScanningRef.current) {
          mediaStream.getTracks().forEach(track => track.stop());
          return; // Don't start scanning if already stopped
        }
        
        const video = document.getElementById('qr-reader-video');
        if (video && isScanningRef.current) {
          video.srcObject = mediaStream;
          
          // Set up video event handlers for mobile
          const handleLoadedMetadata = () => {
            video.play().catch(err => {
              console.error('Error playing video:', err);
              setError('Failed to start camera. Please try again.');
            });
          };
          
          const handlePlay = () => {
            // Video is playing, start detection
            // Initialize BarcodeDetector (browser API)
            // eslint-disable-next-line no-undef
            const detector = new BarcodeDetector({ formats: ['qr_code'] });
            
            // Start scanning loop
            const interval = setInterval(async () => {
              // Check if scanning was stopped (ref will be null)
              // If so, clear this interval and stop execution
              if (!scanningIntervalRef.current || !isScanningRef.current) {
                clearInterval(interval);
                return;
              }
              
              if (video.readyState === video.HAVE_ENOUGH_DATA && !video.paused && !video.ended) {
                try {
                  const barcodes = await detector.detect(video);
                  if (barcodes.length > 0) {
                    const qrData = barcodes[0].rawValue;
                    handleQrScanned(qrData);
                  }
                } catch (err) {
                  // Ignore detection errors
                }
              }
            }, 500); // Check every 500ms
            
            scanningIntervalRef.current = interval;
          };
          
          video.addEventListener('loadedmetadata', handleLoadedMetadata, { once: true });
          video.addEventListener('play', handlePlay, { once: true });
          
          // Try to play immediately as well
          video.play().catch(err => {
            console.error('Error playing video:', err);
            // Will retry on loadedmetadata
          });
        }
      }, 300); // Increased timeout for mobile devices
    } catch (err) {
      console.error('Error starting QR scanner:', err);
      setError('Failed to start camera. Please check permissions.');
      isScanningRef.current = false;
    }
  };

  // Cleanup QR scanner on unmount or close
  useEffect(() => {
    if (!open) {
      handleStopScan();
    }
    return () => {
      handleStopScan();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  
  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Determine which flex and camber fields to show based on brand
  const showFischerFields = form.brand === 'Fischer';
  const showSalomonAtomicFields = ['Salomon', 'Atomic'].includes(form.brand);
  const showRossignolFields = form.brand === 'Rossignol';
  // const showMadshusFields = form.brand === 'Madshus'

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle>{initial ? 'Edit Ski' : 'Add Ski'}</DialogTitle>
      <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
        <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField label="Ski Number" name="ski_number" type="text" value={form.ski_number} onChange={handleChange} required fullWidth />
          <TextField label="Serial Number" name="serial_number" type="text" value={form.serial_number} onChange={handleChange} fullWidth />
          <TextField select label="Brand" name="brand" value={form.brand} onChange={handleChange} required fullWidth>
            {BRANDS.map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}
          </TextField>
          
          {/* QR Code Scanner for Fischer 2025 skis */}
          {!initial && form.brand === 'Fischer' && (
            <Box>
              {!showQrScanner ? (
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => {
                    setShowQrScanner(true);
                    // Use setTimeout to allow React to render the video element first
                    setTimeout(() => handleStartScan(), 200);
                  }}
                  sx={{ mb: 2 }}
                >
                  Scan Fischer QR Code (2025+)
                </Button>
              ) : (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ 
                    width: '100%', 
                    mb: 1, 
                    position: 'relative', 
                    bgcolor: 'black', 
                    borderRadius: 2, 
                    overflow: 'hidden',
                    minHeight: '300px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <video
                      id="qr-reader-video"
                      autoPlay
                      playsInline
                      muted
                      style={{ 
                        width: '100%', 
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                        minHeight: '300px'
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '250px',
                        height: '250px',
                        border: '2px solid rgba(255, 255, 255, 0.8)',
                        borderRadius: 2,
                        pointerEvents: 'none',
                        zIndex: 1
                      }}
                    />
                  </Box>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={handleStopScan}
                    color="error"
                  >
                    Stop Scanning
                  </Button>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Point your camera at the QR code on the Fischer ski label
                  </Typography>
                </Box>
              )}
            </Box>
          )}
          
          {/* Fischer-specific fields */}
          {showFischerFields && (
            <>
              <TextField label="FA (Fischer Flex)" name="fa" type="number" value={form.fa} onChange={handleChange} fullWidth />
              <TextField label="HR (Fischer Camber Height)" name="hr" type="number" value={form.hr} onChange={handleChange} fullWidth />
            </>
          )}
          
          {/* Salomon/Atomic-specific fields */}
          {showSalomonAtomicFields && (
            <>
              <TextField label="MF (Salomon/Atomic Flex)" name="mf" type="number" value={form.mf} onChange={handleChange} fullWidth />
              <TextField label="H4 (Salomon/Atomic Camber Height)" name="h4" type="number" value={form.h4} onChange={handleChange} fullWidth />
            </>
          )}

          {/* Rossignol-specific fields */}
          {showRossignolFields && (
            <>
              <TextField label="Flex (Rossignol Flex)" name="rossi_flex" type="number" value={form.rossi_flex} onChange={handleChange} fullWidth />
              <TextField label="Height (Rossignol Camber Height)" name="rossi_height" type="number" value={form.rossi_height} onChange={handleChange} fullWidth />
              <TextField select label="Mold (Rossignol Name)" name="mold" value={form.mold} onChange={handleChange} fullWidth>
                {/* Skate Options */}
                <MenuItem value="S1">S1</MenuItem>
                <MenuItem value="S2">S2</MenuItem>
                <MenuItem value="S3">S3</MenuItem>
                <MenuItem value="SX">SX</MenuItem>
                {/* Classic Options */}
                <MenuItem value="C1">C1</MenuItem>
                <MenuItem value="C2">C2</MenuItem>
                <MenuItem value="C3">C3</MenuItem>
                <MenuItem value="Zero">Zero</MenuItem>
                {/* Skin Options */}
                <MenuItem value="skin">Skin</MenuItem>
                {/* Double Pole */}
                <MenuItem value="Double Pole">Double Pole</MenuItem>
              </TextField>
              <TextField select label="Stiffness" name="rossi_stiffness" value={form.rossi_stiffness} onChange={handleChange} fullWidth>
                <MenuItem value="Soft">Soft</MenuItem>
                <MenuItem value="Medium">Medium</MenuItem>
                <MenuItem value="Stiff">Stiff</MenuItem>
              </TextField>
              {/* <TextField label="Construction (Rossignol Construction)" name="construction" value={form.construction} onChange={handleChange} fullWidth /> */}
            </>
          )}

          {/* Madshus-specific fields */}
          {/* {showMadshusFields && (
            <>
              <TextField label="Flex (Madshus Flex)" name="flex" type="number" value={form.mf} onChange={handleChange} required fullWidth />
            </>
          )} */}
          
          <TextField label="Grind" name="grind" value={form.grind} onChange={handleChange} fullWidth />
          {!showRossignolFields && <TextField label="Mold" name="mold" value={form.mold} onChange={handleChange} fullWidth />}
          <TextField label="Base" name="base" value={form.base} onChange={handleChange} fullWidth />
          <TextField label="Length (cm)" name="ski_length_cm" type="number" value={form.ski_length_cm} onChange={handleChange} fullWidth />
          <TextField label="Year Made" name="year_made" type="number" value={form.year_made} onChange={handleChange} fullWidth />
          <TextField select label="Type" name="type" value={form.type} onChange={handleChange} required fullWidth>
            <MenuItem value="classic">Classic</MenuItem>
            <MenuItem value="skate">Skate</MenuItem>
          </TextField>
          
          {/* Ski Category */}
          <TextField select label="Ski Category" name="ski_category" value={form.ski_category || ''} onChange={handleChange} fullWidth>
            <MenuItem value="">None</MenuItem>
            <MenuItem value="race">Race</MenuItem>
            <MenuItem value="retired_race">Retired Race</MenuItem>
            <MenuItem value="training">Training</MenuItem>
          </TextField>
          
          {/* Notes */}
          <TextField 
            label="Notes" 
            name="notes" 
            value={form.notes || ''} 
            onChange={handleChange} 
            multiline 
            rows={4} 
            fullWidth 
            placeholder="Add any notes about this ski..."
          />
          
          {/* Temperature Color Slider */}
          <Box>
            <Typography gutterBottom>
              Temperature Color: {form.temperature_color !== null && form.temperature_color !== undefined ? Math.round(form.temperature_color) : 'Not set'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="caption" sx={{ minWidth: 50, color: 'primary.main' }}>Cold</Typography>
              <Slider
                value={form.temperature_color !== null && form.temperature_color !== undefined ? form.temperature_color : 50}
                onChange={(e, value) => setForm(f => ({ ...f, temperature_color: value }))}
                min={0}
                max={100}
                step={1}
                sx={{
                  flex: 1,
                  '& .MuiSlider-thumb': {
                    backgroundColor: form.temperature_color !== null && form.temperature_color !== undefined
                      ? `hsl(${210 - (form.temperature_color * 2.1)}, 70%, 50%)`
                      : 'gray',
                    width: 20,
                    height: 20
                  },
                  '& .MuiSlider-track': {
                    background: 'linear-gradient(to right, #2196f3 0%, #ff5722 100%)',
                    height: 6
                  },
                  '& .MuiSlider-rail': {
                    background: 'linear-gradient(to right, #2196f3 0%, #ff5722 100%)',
                    opacity: 0.3,
                    height: 6
                  }
                }}
              />
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  border: '2px solid',
                  borderColor: 'divider',
                  backgroundColor: form.temperature_color !== null && form.temperature_color !== undefined
                    ? `hsl(${210 - (form.temperature_color * 2.1)}, 70%, 50%)`
                    : 'grey.400',
                  boxShadow: 2
                }}
                title={`Temperature: ${form.temperature_color !== null && form.temperature_color !== undefined ? Math.round(form.temperature_color) : 'Not set'}`}
              />
              <Typography variant="caption" sx={{ minWidth: 50, color: 'error.main' }}>Warm</Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Use this to indicate if the ski is better for cold (blue) or warm (red) conditions
            </Typography>
          </Box>
          
          {error && <Typography color="error">{error}</Typography>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={saving}>
          {saving ? <CircularProgress size={22} color="inherit" /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function SkiDetailModal({ open, onClose, ski, skis, setSkis }) {
  const [tab, setTab] = useState(0);
  const [tests, setTests] = useState([]);
  const [races, setRaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testDetail, setTestDetail] = useState(null);
  const [testDetailOpen, setTestDetailOpen] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const appOrigin = typeof window !== 'undefined' ? window.location.origin : '';
  const qrUrl = ski?.qr_code ? `${appOrigin}/ski/${ski.qr_code}` : '';

  useEffect(() => {
    if (open && ski) {
      setLoading(true);
      Promise.all([getTests(), getRaces(), getSkis()]).then(([allTests, allRaces, allSkis]) => {
        setTests(allTests.filter(t => t.ski_ids_ranked && ski && t.ski_ids_ranked.includes(ski.id)));
        setRaces(allRaces.filter(r => ski && r.ski_id === ski.id));
        setSkis(allSkis);
        setLoading(false);
      });
    } else {
      setTests([]);
      setRaces([]);
      // Do not clear setSkis here!
    }
  }, [open, ski, setSkis]);

  const handleOpenTestDetail = (test) => {
    setTestDetail(test);
    setTestDetailOpen(true);
  };
  const handleCloseTestDetail = () => {
    setTestDetailOpen(false);
    setTestDetail(null);
  };

  const handleRegenerate = async () => {
    if (!ski) return;
    try {
      setRegenerating(true);
      await regenerateSkiQr(ski.id);
      const allSkis = await getSkis();
      setSkis(allSkis);
    } finally {
      setRegenerating(false);
    }
  };


  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography component="span">Ski #{ski?.ski_number} Details</Typography>
          <IconButton
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            size="small"
            edge="end"
          >
            <MoreVertIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label="Info" />
          <Tab label="Tests" />
          <Tab label="Races" />
        </Tabs>
        {tab === 0 && ski && (
          <TableContainer component={Paper} sx={{ my: 2, borderRadius: 2, boxShadow: 1 }}>
            <Table size="small">
              <TableBody>
                <TableRow><TableCell sx={{ fontWeight: 600 }}>Brand</TableCell><TableCell>{ski.brand}</TableCell></TableRow>
                {ski.brand === 'Fischer' && (
                  <>
                    <TableRow><TableCell sx={{ fontWeight: 600 }}>FA (Flex)</TableCell><TableCell>{ski.fa}</TableCell></TableRow>
                    <TableRow><TableCell sx={{ fontWeight: 600 }}>HR (Camber Height)</TableCell><TableCell>{ski.hr}</TableCell></TableRow>
                  </>
                )}
                {['Salomon', 'Atomic'].includes(ski.brand) && (
                  <>
                    <TableRow><TableCell sx={{ fontWeight: 600 }}>MF (Flex)</TableCell><TableCell>{ski.mf}</TableCell></TableRow>
                    <TableRow><TableCell sx={{ fontWeight: 600 }}>H4 (Camber Height)</TableCell><TableCell>{ski.h4}</TableCell></TableRow>
                  </>
                )}
                {ski.brand === 'Rossignol' && (
                  <>
                    <TableRow><TableCell sx={{ fontWeight: 600 }}>Flex</TableCell><TableCell>{ski.rossi_flex}</TableCell></TableRow>
                    <TableRow><TableCell sx={{ fontWeight: 600 }}>Height</TableCell><TableCell>{ski.rossi_height}</TableCell></TableRow>
                    <TableRow><TableCell sx={{ fontWeight: 600 }}>Stiffness</TableCell><TableCell>{ski.rossi_stiffness}</TableCell></TableRow>
                  </>
                )}
                <TableRow><TableCell sx={{ fontWeight: 600 }}>Grind</TableCell><TableCell>{ski.grind}</TableCell></TableRow>
                <TableRow><TableCell sx={{ fontWeight: 600 }}>Mold</TableCell><TableCell>{ski.mold}</TableCell></TableRow>
                <TableRow><TableCell sx={{ fontWeight: 600 }}>Base</TableCell><TableCell>{ski.base}</TableCell></TableRow>
                <TableRow><TableCell sx={{ fontWeight: 600 }}>Length</TableCell><TableCell>{ski.ski_length_cm}</TableCell></TableRow>
                <TableRow><TableCell sx={{ fontWeight: 600 }}>Year Made</TableCell><TableCell>{ski.year_made}</TableCell></TableRow>
                <TableRow><TableCell sx={{ fontWeight: 600 }}>Type</TableCell><TableCell>{ski.type}</TableCell></TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Temperature Color</TableCell>
                  <TableCell>
                    {ski.temperature_color !== null && ski.temperature_color !== undefined ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 30,
                            height: 30,
                            borderRadius: '50%',
                            backgroundColor: `hsl(${210 - (ski.temperature_color * 2.1)}, 70%, 50%)`,
                            border: '1px solid rgba(0,0,0,0.1)',
                            boxShadow: 1
                          }}
                        />
                        <Typography>{Math.round(ski.temperature_color)} (0=cold, 100=warm)</Typography>
                      </Box>
                    ) : (
                      <Typography color="text.secondary">Not set</Typography>
                    )}
                  </TableCell>
                </TableRow>
                <TableRow><TableCell sx={{ fontWeight: 600 }}>Serial Number</TableCell><TableCell>{ski.serial_number}</TableCell></TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>QR Code</TableCell>
                  <TableCell>
                    {ski.qr_code ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <QRCodeDisplay url={qrUrl} title={`Ski ${ski.ski_number}`} />
                        <Button size="small" variant="outlined" onClick={async () => { await printSingleSkiQrLabels(ski); }}>Print Labels</Button>
                        {/* <Button size="small" variant="outlined" onClick={handleRegenerate} disabled={regenerating}>Regenerate</Button> */}
                      </Box>
                    ) : (
                      <Button size="small" variant="outlined" onClick={handleRegenerate} disabled={regenerating}>Generate QR</Button>
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                  <TableCell>
                    <Typography>{ski.ski_category ? ski.ski_category.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Not set'}</Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, verticalAlign: 'top' }}>Notes</TableCell>
                  <TableCell>
                    <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                      {ski.notes || 'No notes'}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {tab === 1 && (
          <Box>
            <Typography variant="subtitle1">Test History</Typography>
            {loading ? <Typography>Loading...</Typography> :
              (!ski || tests.length === 0) ? <Typography>No tests for this ski yet.</Typography> :
                <TableContainer component={Paper} sx={{ my: 2, borderRadius: 2, boxShadow: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Location</TableCell>
                        <TableCell>Ranking</TableCell>
                        <TableCell>More</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tests.map((test) => (
                        <TableRow key={test.id}>
                          <TableCell>{test.name}</TableCell>
                          <TableCell>{test.location}</TableCell>
                          <TableCell>{test.ski_ids_ranked && ski ? (test.ski_ids_ranked.indexOf(ski.id) + 1) : '-'}</TableCell>
                          <TableCell>
                            <Button size="small" onClick={() => handleOpenTestDetail(test)}>Info</Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
            }
            <TestDetailModal open={testDetailOpen} onClose={handleCloseTestDetail} test={testDetail} skis={skis} />
          </Box>
        )}
        {tab === 2 && (
          <Box>
            <Typography variant="subtitle1">Race History</Typography>
            {loading ? <Typography>Loading...</Typography> :
              (!ski || races.length === 0) ? <Typography>No races for this ski yet.</Typography> :
                <TableContainer component={Paper} sx={{ my: 2, borderRadius: 2, boxShadow: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Location</TableCell>
                        <TableCell>Temp</TableCell>
                        <TableCell>Ski Used</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {races.map((race) => (
                        <TableRow key={race.id}>
                          <TableCell>{race.name}</TableCell>
                          <TableCell>{race.location}</TableCell>
                          <TableCell>{race.temperature}</TableCell>
                          <TableCell>{ski ? ski.ski_number : ''}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
            }
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem onClick={() => {
          setMenuAnchor(null);
          setTransferDialogOpen(true);
        }}>
          Transfer Ownership
        </MenuItem>
      </Menu>
      <TransferOwnershipDialog
        open={transferDialogOpen}
        onClose={() => setTransferDialogOpen(false)}
        ski={ski}
        onSuccess={() => {
          // Refresh skis after transfer is sent
          getSkis().then(setSkis);
        }}
      />
    </Dialog>
  );
}

function DeleteSkiConfirmDialog({ open, onClose, onConfirm, skiNumber }) {
  const [input, setInput] = useState('');
  const handleChange = (e) => setInput(e.target.value);
  const handleConfirm = () => {
    onConfirm();
    setInput('');
  };
  const handleClose = () => {
    onClose();
    setInput('');
  };
  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>Confirm Delete</DialogTitle>
      <DialogContent>
        <Typography gutterBottom>
          To delete this ski, type the ski number <b>{skiNumber}</b> below. This action cannot be undone.
        </Typography>
        <TextField
          autoFocus
          margin="dense"
          label="Ski Number"
          fullWidth
          value={input}
          onChange={handleChange}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleConfirm} color="error" variant="contained" disabled={input !== String(skiNumber)}>
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Sortable row component for drag and drop
function SortableRow({ ski, index, isFischer, isSalomonAtomic, isRossignol, isMobile, showCheckBox, selectedIds, onToggleSelect, onDetail, onEdit, handleDeleteClick, isSortActive }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: ski.id, disabled: isSortActive });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Disable drag when sort is active
  const dragAttributes = isSortActive ? {} : attributes;
  const dragListeners = isSortActive ? {} : listeners;

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      hover
      sx={{ ...(isDragging && { bgcolor: 'action.selected' }) }}
    >
      {showCheckBox && 
        <TableCell padding="checkbox">
          <Checkbox color="primary" checked={!!selectedIds?.has(ski.id)} onChange={() => onToggleSelect?.(ski.id)} />
        </TableCell>
      }
      <TableCell sx={{ ...(isMobile && { px: 1, py: 0.5, fontSize: 13, maxWidth: 60, overflow: 'hidden', textOverflow: 'ellipsis' }) }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            {...dragAttributes}
            {...dragListeners}
            sx={{ 
              cursor: isSortActive ? 'not-allowed' : 'grab', 
              p: 0.5,
              display: 'flex',
              alignItems: 'center',
              borderRadius: 1,
              opacity: isSortActive ? 0.5 : 1,
              '&:active': { cursor: isSortActive ? 'not-allowed' : 'grabbing', bgcolor: 'action.selected' },
              '&:hover': { bgcolor: isSortActive ? 'transparent' : 'action.hover' }
            }}
            title={isSortActive ? 'Disable sorting to reorder skis' : 'Drag to reorder'}
          >
            <DragIndicatorIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          </Box>
          {ski.ski_number}
        </Box>
      </TableCell>
      {isFischer && (
        <>
          <TableCell sx={isMobile ? { px: 1, py: 0.5, fontSize: 13, maxWidth: 50, overflow: 'hidden', textOverflow: 'ellipsis' } : {}}>{ski.fa}</TableCell>
          <TableCell sx={isMobile ? { px: 1, py: 0.5, fontSize: 13, maxWidth: 50, overflow: 'hidden', textOverflow: 'ellipsis' } : {}}>{ski.hr}</TableCell>
        </>
      )}
      {isSalomonAtomic && (
        <>
          <TableCell sx={isMobile ? { px: 1, py: 0.5, fontSize: 13, maxWidth: 50, overflow: 'hidden', textOverflow: 'ellipsis' } : {}}>{ski.mf}</TableCell>
          <TableCell sx={isMobile ? { px: 1, py: 0.5, fontSize: 13, maxWidth: 50, overflow: 'hidden', textOverflow: 'ellipsis' } : {}}>{ski.h4}</TableCell>
        </>
      )}
      {isRossignol && (
        <>
          <TableCell sx={isMobile ? { px: 1, py: 0.5, fontSize: 13, maxWidth: 50, overflow: 'hidden', textOverflow: 'ellipsis' } : {}}>{ski.rossi_flex}</TableCell>
          <TableCell sx={isMobile ? { px: 1, py: 0.5, fontSize: 13, maxWidth: 50, overflow: 'hidden', textOverflow: 'ellipsis' } : {}}>{ski.rossi_height}</TableCell>
        </>
      )}
      <TableCell sx={isMobile ? { px: 1, py: 0.5, fontSize: 13, maxWidth: 60, overflow: 'hidden', textOverflow: 'ellipsis' } : {}}>{ski.grind}</TableCell>
      <TableCell sx={isMobile ? { px: 1, py: 0.5, fontSize: 13, maxWidth: 60, overflow: 'hidden', textOverflow: 'ellipsis' } : {}}>{ski.mold}</TableCell>
      <TableCell sx={isMobile ? { px: 1, py: 0.5, fontSize: 13, maxWidth: 60, overflow: 'hidden', textOverflow: 'ellipsis' } : {}}>{ski.base}</TableCell>
      <TableCell sx={isMobile ? { px: 1, py: 0.5, fontSize: 13, maxWidth: 60, overflow: 'hidden', textOverflow: 'ellipsis' } : {}}>{ski.year_made}</TableCell>
      <TableCell align="center" sx={isMobile ? { px: 1, py: 0.5, fontSize: 13 } : {}}>
        {ski.temperature_color !== null && ski.temperature_color !== undefined ? (
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: '50%',
              mx: 'auto',
              backgroundColor: `hsl(${210 - (ski.temperature_color * 2.1)}, 70%, 50%)`,
              border: '1px solid rgba(0,0,0,0.1)',
              boxShadow: 1
            }}
            title={`Temperature: ${Math.round(ski.temperature_color)} (0=cold, 100=warm)`}
          />
        ) : (
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: '50%',
              mx: 'auto',
              backgroundColor: 'grey.300',
              border: '1px solid rgba(0,0,0,0.1)'
            }}
            title="Temperature not set"
          />
        )}
      </TableCell>
      <TableCell align="center" sx={isMobile ? { px: 1, py: 0.5, fontSize: 13 } : {}}>
        <Stack direction="row" spacing={isMobile ? 1.5 : 1} justifyContent="center">
          <IconButton size={isMobile ? 'medium' : 'small'} onClick={() => onDetail(ski)}><InfoIcon fontSize={isMobile ? 'medium' : 'small'} /></IconButton>
          <IconButton size={isMobile ? 'medium' : 'small'} onClick={() => onEdit(ski)}><EditIcon fontSize={isMobile ? 'medium' : 'small'} /></IconButton>
          <IconButton size={isMobile ? 'medium' : 'small'} color="error" onClick={() => handleDeleteClick(ski)}><DeleteIcon fontSize={isMobile ? 'medium' : 'small'} /></IconButton>
        </Stack>
      </TableCell>
    </TableRow>
  );
}

function SkisTable({ skis, onEdit, onDelete, onDetail, selectedIds, onToggleSelect, showCheckBox, setSkis }) {
  const [deleteDialog, setDeleteDialog] = useState({ open: false, ski: null });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [localSkis, setLocalSkis] = useState(skis);
  const skipNextPropUpdate = useRef(false);
  const pendingUpdateRef = useRef(null); // Track pending server update
  const debounceTimerRef = useRef(null); // Track debounce timer
  const pendingOrdersRef = useRef(null); // Track the most recent order changes

  // Update local skis when prop changes, but skip if we just did an optimistic update
  // Also only update if the data actually changed to prevent unnecessary re-renders
  useEffect(() => {
    if (skipNextPropUpdate.current) {
      skipNextPropUpdate.current = false;
      return;
    }
    
    // Only update if the ski list or display orders actually changed
    const currentIds = new Set(localSkis.map(s => s.id));
    const newIds = new Set(skis.map(s => s.id));
    const idsChanged = currentIds.size !== newIds.size || 
      ![...currentIds].every(id => newIds.has(id));
    
    if (idsChanged) {
      setLocalSkis(skis);
      return;
    }
    
    // Check if display orders changed
    const currentOrderMap = new Map(localSkis.map(s => [s.id, s.display_order]));
    const newOrderMap = new Map(skis.map(s => [s.id, s.display_order]));
    const ordersChanged = localSkis.some(ski => {
      const newOrder = newOrderMap.get(ski.id);
      const currentOrder = currentOrderMap.get(ski.id);
      return newOrder !== currentOrder;
    });
    
    if (ordersChanged) {
      setLocalSkis(skis);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skis]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Separate sorting state for each brand
  const [sortStates, setSortStates] = useState({});

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleSort = (brand, property) => {
    const currentState = sortStates[brand] || { orderBy: null, order: null };
    
    // Three-state cycle: asc -> desc -> no sort (null)
    let newOrder;
    if (currentState.orderBy === property) {
      if (currentState.order === 'asc') {
        newOrder = 'desc';
      } else if (currentState.order === 'desc') {
        newOrder = null; // No sort - back to display_order
      } else {
        newOrder = 'asc'; // Shouldn't happen, but fallback
      }
    } else {
      // Different property, start with asc
      newOrder = 'asc';
    }
    
    setSortStates(prev => ({
      ...prev,
      [brand]: { orderBy: newOrder ? property : null, order: newOrder }
    }));
  };

  const handleDeleteClick = (ski) => setDeleteDialog({ open: true, ski });
  const handleDeleteConfirm = () => {
    onDelete(deleteDialog.ski.id);
    setDeleteDialog({ open: false, ski: null });
  };
  const handleDeleteCancel = () => setDeleteDialog({ open: false, ski: null });

  // Handle drag end for reordering
  const handleDragEnd = async (event, brandSkis, brand) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) {
      return;
    }

    // Safety check: prevent drag-and-drop when a sort is active
    const brandSortState = sortStates[brand] || { orderBy: null, order: null };
    if (brandSortState.order !== null) {
      console.warn('Drag-and-drop is disabled when sorting is active. Please clear the sort first.');
      return;
    }

    const oldIndex = brandSkis.findIndex(ski => ski.id === active.id);
    const newIndex = brandSkis.findIndex(ski => ski.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Get the type of the dragged ski to ensure we only reorder within the same brand/type group
    const draggedSki = brandSkis[oldIndex];
    const skiType = draggedSki.type;
    
    // Filter to only skis of the same brand AND type
    const brandTypeSkis = brandSkis.filter(ski => ski.type === skiType);
    
    // Find indices within the filtered group
    const typeOldIndex = brandTypeSkis.findIndex(ski => ski.id === active.id);
    const typeNewIndex = brandTypeSkis.findIndex(ski => ski.id === over.id);
    
    if (typeOldIndex === -1 || typeNewIndex === -1) {
      return; // Can't drag between different types
    }

    // Calculate the new display_order values based on the drag
    const reorderedSkis = arrayMove(brandTypeSkis, typeOldIndex, typeNewIndex);
    const skiOrders = reorderedSkis.map((ski, index) => ({
      id: ski.id,
      display_order: index + 1
    }));

    // Update local state optimistically for instant visual feedback
    // Use flushSync to ensure this renders synchronously before any other updates
    const updatedLocalSkis = localSkis.map(ski => {
      const orderUpdate = skiOrders.find(so => so.id === ski.id);
      if (orderUpdate && ski.brand === brand && ski.type === skiType) {
        return { ...ski, display_order: orderUpdate.display_order };
      }
      return ski;
    });
    
    // Prevent useEffect from overwriting our optimistic update
    skipNextPropUpdate.current = true;
    // Use flushSync to ensure this update happens synchronously and smoothly
    flushSync(() => {
      setLocalSkis(updatedLocalSkis);
    });

    // Cancel any pending server update and debounce timer
    if (pendingUpdateRef.current) {
      // Cancel the previous update (if it's a promise, we can't cancel it, but we'll ignore its result)
      pendingUpdateRef.current = null;
    }
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    // Store the most recent order changes
    pendingOrdersRef.current = { skiOrders, updatedLocalSkis, brand, skiType };

    // Debounce the server update - wait 300ms after the last drag before sending
    // This batches rapid drag operations into a single update
    debounceTimerRef.current = setTimeout(() => {
      const { skiOrders: finalOrders, updatedLocalSkis: finalLocalSkis } = pendingOrdersRef.current || {};
      
      if (!finalOrders) {
        debounceTimerRef.current = null;
        return;
      }

      // Clear the pending ref
      pendingOrdersRef.current = null;
      debounceTimerRef.current = null;

      // Save to backend
      const updatePromise = updateSkiOrder(finalOrders)
        .then(async () => {
          // Only process if this is still the latest update
          if (pendingUpdateRef.current === updatePromise) {
            pendingUpdateRef.current = null;
            
            if (setSkis) {
              const freshSkis = await getSkis();
              // Compare display orders - only update if they're different
              const currentOrderMap = new Map(finalLocalSkis.map(s => [s.id, s.display_order]));
              const freshOrderMap = new Map(freshSkis.map(s => [s.id, s.display_order]));
              const ordersMatch = finalLocalSkis.every(ski => {
                const freshOrder = freshOrderMap.get(ski.id);
                const currentOrder = currentOrderMap.get(ski.id);
                return freshOrder === currentOrder || (freshOrder === undefined && currentOrder === undefined);
              });
              
              // Reset the flag BEFORE calling setSkis to avoid race condition
              // When setSkis triggers a re-render, the useEffect will run and should process the update
              skipNextPropUpdate.current = false;
              
              if (!ordersMatch || freshSkis.length !== finalLocalSkis.length) {
                // Orders don't match or ski count changed, need to sync
                // Defer to next frame to avoid glitch
                requestAnimationFrame(() => {
                  setSkis(freshSkis);
                });
              } else {
                // Orders match - optimistic update was correct
                setSkis(freshSkis);
              }
            } else {
              skipNextPropUpdate.current = false;
            }
          }
        })
        .catch((err) => {
          // Only process error if this is still the latest update
          if (pendingUpdateRef.current === updatePromise) {
            console.error('Error updating ski order:', err);
            pendingUpdateRef.current = null;
            
            // On error, revert by refreshing from server
            // Reset flag before setSkis to avoid race condition
            if (setSkis) {
              getSkis().then(freshSkis => {
                skipNextPropUpdate.current = false;
                setSkis(freshSkis);
              });
            } else {
              skipNextPropUpdate.current = false;
            }
          }
        });

      // Track this as the current pending update
      pendingUpdateRef.current = updatePromise;
    }, 300); // 300ms debounce - adjust if needed
  };

  // Group skis by brand and sort brands by number of skis (most to least)
  const groupSkisByBrand = (skisToGroup) => {
    const groups = {};
    skisToGroup.forEach(ski => {
      if (!groups[ski.brand]) {
        groups[ski.brand] = [];
      }
      groups[ski.brand].push(ski);
    });
    
    // Sort brands by number of skis (most to least)
    return Object.entries(groups).sort(([, a], [, b]) => b.length - a.length);
  };

  const renderBrandTable = (brandSkis, brand) => {
    // Use localSkis to ensure we're working with the most up-to-date data
    const brandSkisFromLocal = localSkis.filter(ski => ski.brand === brand && brandSkis.some(bs => bs.id === ski.id));
    const actualBrandSkis = brandSkisFromLocal.length > 0 ? brandSkisFromLocal : brandSkis;
    const isFischer = brand === 'Fischer';
    const isSalomonAtomic = ['Salomon', 'Atomic'].includes(brand);
    const isRossignol = brand === 'Rossignol'
    
    // Get sorting state for this brand
    const brandSortState = sortStates[brand] || { orderBy: null, order: null };
    
    // Sort skis within this brand table
    // If sorting by FA/HR/MF/H4, temporarily override display_order
    // Otherwise, use display_order as primary sort
    const sortedBrandSkis = [...actualBrandSkis].sort((a, b) => {
      // If no sort is active (order is null), use display_order only
      if (!brandSortState.order || !brandSortState.orderBy) {
        const aOrder = a.display_order !== null && a.display_order !== undefined ? a.display_order : 999999;
        const bOrder = b.display_order !== null && b.display_order !== undefined ? b.display_order : 999999;
        if (aOrder !== bOrder) {
          return aOrder - bOrder;
        }
        // Tiebreaker: ski_number
        const aNum = parseInt(a.ski_number) || 0;
        const bNum = parseInt(b.ski_number) || 0;
        return aNum - bNum;
      }
      
      const isNumericSort = brandSortState.orderBy === 'fa' || brandSortState.orderBy === 'hr' || 
                           brandSortState.orderBy === 'mf' || brandSortState.orderBy === 'h4' ||
                           brandSortState.orderBy === 'rossi_flex' || brandSortState.orderBy === 'rossi_height';
      
      if (isNumericSort) {
        // When sorting by numeric fields, use that as primary sort (temporarily override display_order)
        const aVal = parseFloat(a[brandSortState.orderBy]) || 0;
        const bVal = parseFloat(b[brandSortState.orderBy]) || 0;
        const numericResult = brandSortState.order === 'asc' ? aVal - bVal : bVal - aVal;
        // If values are equal, use display_order as tiebreaker
        if (numericResult !== 0) {
          return numericResult;
        }
        // Tiebreaker: display_order
        const aOrder = a.display_order !== null && a.display_order !== undefined ? a.display_order : 999999;
        const bOrder = b.display_order !== null && b.display_order !== undefined ? b.display_order : 999999;
        return aOrder - bOrder;
      } else {
        // Default: display_order as primary, then selected field as secondary
        const aOrder = a.display_order !== null && a.display_order !== undefined ? a.display_order : 999999;
        const bOrder = b.display_order !== null && b.display_order !== undefined ? b.display_order : 999999;
        if (aOrder !== bOrder) {
          return aOrder - bOrder;
        }
        // Secondary sort: ski_number
        const aNum = parseInt(a.ski_number) || 0;
        const bNum = parseInt(b.ski_number) || 0;
        return brandSortState.order === 'asc' ? aNum - bNum : bNum - aNum;
      }
    });
    
    // Mobile card view
    if (isMobile) {
      return (
        <Box key={brand} sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
            {brand} Skis ({brandSkis.length})
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {sortedBrandSkis.map((ski) => (
              <Accordion key={ski.id} sx={{ boxShadow: 2 }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ 
                    '&:hover': { backgroundColor: 'action.hover' },
                    minHeight: 56,
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Ski #{ski.ski_number}
                    </Typography>
                    <Stack 
                      direction="row" 
                      spacing={0.5} 
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      onTouchStart={(e) => e.stopPropagation()}
                    >
                      <IconButton 
                        size="small" 
                        component="div"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDetail(ski);
                        }}
                        sx={{ cursor: 'pointer' }}
                      >
                        <InfoIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        component="div"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(ski);
                        }}
                        sx={{ cursor: 'pointer' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        color="error" 
                        component="div"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(ski);
                        }}
                        sx={{ cursor: 'pointer' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {isFischer && (
                      <>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">FA:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{ski.fa || '-'}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">HR:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{ski.hr || '-'}</Typography>
                        </Box>
                      </>
                    )}
                    {isSalomonAtomic && (
                      <>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">MF:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{ski.mf || '-'}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">H4:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{ski.h4 || '-'}</Typography>
                        </Box>
                      </>
                    )}
                    {isRossignol && (
                      <>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Flex:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{ski.rossi_flex || '-'}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Height:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{ski.rossi_height || '-'}</Typography>
                        </Box>
                      </>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Grind:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{ski.grind || '-'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Mold:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{ski.mold || '-'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Base:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{ski.base || '-'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Year:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{ski.year_made || '-'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">Temp:</Typography>
                      {ski.temperature_color !== null && ski.temperature_color !== undefined ? (
                        <Box
                          sx={{
                            width: 30,
                            height: 30,
                            borderRadius: '50%',
                            backgroundColor: `hsl(${210 - (ski.temperature_color * 2.1)}, 70%, 50%)`,
                            border: '1px solid rgba(0,0,0,0.1)',
                            boxShadow: 1
                          }}
                          title={`Temperature: ${Math.round(ski.temperature_color)} (0=cold, 100=warm)`}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Box>
      );
    }
    
    // Desktop table view
    return (
      <Box key={brand} sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
          {brand} Skis ({brandSkis.length})
        </Typography>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={(event) => handleDragEnd(event, sortedBrandSkis, brand)}
        >
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: 2,
              boxShadow: 2,
              width: '100%',
            }}
          >
            <Table size="medium">
              <TableHead>
                <TableRow sx={{ background: '#f5f5f5' }}>
                  {showCheckBox && <TableCell padding="checkbox"></TableCell>}
                  <TableCell sx={isMobile ? { px: 1, py: 0.5, fontSize: 13 } : {}}>Ski #</TableCell>
                  {isFischer && (
                    <>
                      <TableCell 
                        sx={isMobile ? { px: 1, py: 0.5, fontSize: 13 } : {}} 
                        sortDirection={brandSortState.orderBy === 'fa' && brandSortState.order ? brandSortState.order : false}
                      >
                        {brandSortState.orderBy === 'fa' && brandSortState.order ? (
                          <TableSortLabel
                            active={true}
                            direction={brandSortState.order}
                            onClick={() => handleSort(brand, 'fa')}
                          >
                            FA
                          </TableSortLabel>
                        ) : (
                          <Box
                            onClick={() => handleSort(brand, 'fa')}
                            sx={{ cursor: 'pointer', display: 'inline-block', userSelect: 'none' }}
                          >
                            FA
                          </Box>
                        )}
                      </TableCell>
                      <TableCell 
                        sx={isMobile ? { px: 1, py: 0.5, fontSize: 13 } : {}} 
                        sortDirection={brandSortState.orderBy === 'hr' && brandSortState.order ? brandSortState.order : false}
                      >
                        {brandSortState.orderBy === 'hr' && brandSortState.order ? (
                          <TableSortLabel
                            active={true}
                            direction={brandSortState.order}
                            onClick={() => handleSort(brand, 'hr')}
                          >
                            HR
                          </TableSortLabel>
                        ) : (
                          <Box
                            onClick={() => handleSort(brand, 'hr')}
                            sx={{ cursor: 'pointer', display: 'inline-block', userSelect: 'none' }}
                          >
                            HR
                          </Box>
                        )}
                      </TableCell>
                    </>
                  )}
                  {isSalomonAtomic && (
                    <>
                      <TableCell 
                        sx={isMobile ? { px: 1, py: 0.5, fontSize: 13 } : {}} 
                        sortDirection={brandSortState.orderBy === 'mf' && brandSortState.order ? brandSortState.order : false}
                      >
                        {brandSortState.orderBy === 'mf' && brandSortState.order ? (
                          <TableSortLabel
                            active={true}
                            direction={brandSortState.order}
                            onClick={() => handleSort(brand, 'mf')}
                          >
                            MF
                          </TableSortLabel>
                        ) : (
                          <Box
                            onClick={() => handleSort(brand, 'mf')}
                            sx={{ cursor: 'pointer', display: 'inline-block', userSelect: 'none' }}
                          >
                            MF
                          </Box>
                        )}
                      </TableCell>
                      <TableCell 
                        sx={isMobile ? { px: 1, py: 0.5, fontSize: 13 } : {}} 
                        sortDirection={brandSortState.orderBy === 'h4' && brandSortState.order ? brandSortState.order : false}
                      >
                        {brandSortState.orderBy === 'h4' && brandSortState.order ? (
                          <TableSortLabel
                            active={true}
                            direction={brandSortState.order}
                            onClick={() => handleSort(brand, 'h4')}
                          >
                            H4
                          </TableSortLabel>
                        ) : (
                          <Box
                            onClick={() => handleSort(brand, 'h4')}
                            sx={{ cursor: 'pointer', display: 'inline-block', userSelect: 'none' }}
                          >
                            H4
                          </Box>
                        )}
                      </TableCell>
                    </>
                  )}
                  {isRossignol && (
                    <>
                      <TableCell 
                        sx={isMobile ? { px: 1, py: 0.5, fontSize: 13 } : {}} 
                        sortDirection={brandSortState.orderBy === 'rossi_flex' && brandSortState.order ? brandSortState.order : false}
                      >
                        {brandSortState.orderBy === 'rossi_flex' && brandSortState.order ? (
                          <TableSortLabel
                            active={true}
                            direction={brandSortState.order}
                            onClick={() => handleSort(brand, 'rossi_flex')}
                          >
                            Flex
                          </TableSortLabel>
                        ) : (
                          <Box
                            onClick={() => handleSort(brand, 'rossi_flex')}
                            sx={{ cursor: 'pointer', display: 'inline-block', userSelect: 'none' }}
                          >
                            Flex
                          </Box>
                        )}
                      </TableCell>
                      <TableCell 
                        sx={isMobile ? { px: 1, py: 0.5, fontSize: 13 } : {}} 
                        sortDirection={brandSortState.orderBy === 'rossi_height' && brandSortState.order ? brandSortState.order : false}
                      >
                        {brandSortState.orderBy === 'rossi_height' && brandSortState.order ? (
                          <TableSortLabel
                            active={true}
                            direction={brandSortState.order}
                            onClick={() => handleSort(brand, 'rossi_height')}
                          >
                            Height
                          </TableSortLabel>
                        ) : (
                          <Box
                            onClick={() => handleSort(brand, 'rossi_height')}
                            sx={{ cursor: 'pointer', display: 'inline-block', userSelect: 'none' }}
                          >
                            Height
                          </Box>
                        )}
                      </TableCell>
                    </>
                  )}
                  <TableCell sx={isMobile ? { px: 1, py: 0.5, fontSize: 13 } : {}}>Grind</TableCell>
                  <TableCell sx={isMobile ? { px: 1, py: 0.5, fontSize: 13 } : {}}>Mold</TableCell>
                  <TableCell sx={isMobile ? { px: 1, py: 0.5, fontSize: 13 } : {}}>Base</TableCell>
                  <TableCell sx={isMobile ? { px: 1, py: 0.5, fontSize: 13 } : {}}>Year</TableCell>
                  <TableCell sx={isMobile ? { px: 1, py: 0.5, fontSize: 13 } : {}} align="center">Temp</TableCell>
                  <TableCell align="center" sx={isMobile ? { px: 1, py: 0.5, fontSize: 13 } : {}}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <SortableContext
                items={sortedBrandSkis.map(ski => ski.id)}
                strategy={verticalListSortingStrategy}
              >
                <TableBody>
                  {sortedBrandSkis.map((ski, index) => (
                    <SortableRow
                      key={ski.id}
                      ski={ski}
                      index={index}
                      isFischer={isFischer}
                      isSalomonAtomic={isSalomonAtomic}
                      isRossignol={isRossignol}
                      isMobile={isMobile}
                      showCheckBox={showCheckBox}
                      selectedIds={selectedIds}
                      onToggleSelect={onToggleSelect}
                      onDetail={onDetail}
                      onEdit={onEdit}
                      handleDeleteClick={handleDeleteClick}
                      isSortActive={brandSortState.order !== null}
                    />
                  ))}
                </TableBody>
              </SortableContext>
            </Table>
          </TableContainer>
        </DndContext>
      </Box>
    );
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {groupSkisByBrand(localSkis).map(([brand, brandSkis]) => 
        renderBrandTable(brandSkis, brand)
      )}
      
      <DeleteSkiConfirmDialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        skiNumber={deleteDialog.ski?.ski_number}
      />
    </Box>
  );
}

export default function SkisSection() {
  const [skis, setSkis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, ski: null });
  const [detail, setDetail] = useState({ open: false, ski: null });
  const [uploadOpen, setUploadOpen] = useState(false);
  const { user } = useAuth();
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showQRCheckBoxes, setShowQRCheckBoxes] = useState(false);

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // const handlePrintSelected = async () => {
  //   const ids = Array.from(selectedIds);
  //   if (ids.length === 0) return;
  //   const byId = new Map(skis.map(s => [s.id, s]));
  //   const selectedSkis = ids.map(id => byId.get(id)).filter(ski => ski && ski.qr_code);
  //   if (selectedSkis.length > 0) {
  //     await printAllSkiQrLabels(selectedSkis);
  //   }
  // };

  const handleGenerateForSelected = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    const byId = new Map(skis.map(s => [s.id, s]));
    for (const id of ids) {
      const s = byId.get(id);
      if (s && !s.qr_code) {
        try { await regenerateSkiQr(id); } catch {}
      }
    }
    await fetchSkis();

    // After generating QR codes, get the fresh ski data and print the labels for selected skis
    const freshSkis = await getSkis();
    const freshById = new Map(freshSkis.map(s => [s.id, s]));
    const selectedSkis = ids.map(id => freshById.get(id)).filter(ski => ski && ski.qr_code);
    if (selectedSkis.length > 0) {
      await printAllSkiQrLabels(selectedSkis);
    }
  };

  const handleClearSelection = () => setSelectedIds(new Set());

  const fetchSkis = async () => {
    setLoading(true);
    try {
      const data = await getSkis();
      setSkis(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchSkis(); }, []);

  const handleAdd = () => setModal({ open: true, ski: null });
  const handleEdit = ski => setModal({ open: true, ski });
  const handleDelete = async skiId => {
    await deleteSki(skiId);
    fetchSkis();
  };
  const handleSave = async (form) => {
    try {
      if (modal.ski) {
        await updateSki(modal.ski.id, form);
      } else {
        await addSki(form);
      }
      fetchSkis();
      setModal({ open: false, ski: null });
    } catch (error) {
      console.error('Error saving ski:', error);
      if (error.message && error.message.includes('unique')) {
        alert(`Ski number ${form.ski_number} already exists in your fleet. Please choose a different number.`);
      } else {
        alert('Failed to save ski. Please try again.');
      }
    }
  };
  const handleDetail = ski => setDetail({ open: true, ski });
  const handleExport = () => {
    exportSkisToPDF(skis, user);
  };

  const exitQRMode = () => { setShowQRCheckBoxes(false); setSelectedIds(new Set()); };

  const classicSkis = skis.filter(s => s.type === 'classic');
  const skateSkis = skis.filter(s => s.type === 'skate');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box sx={{ mt: 2, px: isMobile ? 1 : 0 }}>
      <SkiTransferNotifications onTransferAccepted={fetchSkis} />
      <SentSkiTransfers onTransferCancelled={fetchSkis} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: isMobile ? 'wrap' : 'nowrap', gap: isMobile ? 1 : 0 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: isMobile ? 'wrap' : 'nowrap', width: isMobile ? '100%' : 'auto' }}>
          <Button 
            variant="outlined" 
            startIcon={!isMobile ? <DownloadIcon /> : null}
            onClick={handleExport}
            disabled={skis.length === 0}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            {isMobile ? 'Export PDF' : 'Export PDF'}
          </Button>

    {!showQRCheckBoxes ? (
      <Button
        variant="outlined"
        onClick={() => setShowQRCheckBoxes(true)}
        fullWidth={isMobile}
        size={isMobile ? 'medium' : 'medium'}
      >
        Generate QR
      </Button>
    ) : (
      <>
        <Button
          variant="outlined"
          onClick={handleGenerateForSelected}
          disabled={selectedIds.size === 0}
          fullWidth={isMobile}
          size={isMobile ? 'medium' : 'medium'}
        >
          Generate QR for Selected
        </Button>
        {selectedIds.size > 0 && (
          <Button variant="text" onClick={handleClearSelection} fullWidth={isMobile} size={isMobile ? 'medium' : 'medium'}>
            Clear Selection
          </Button>
        )}
        <Button variant="text" onClick={exitQRMode} fullWidth={isMobile} size={isMobile ? 'medium' : 'medium'}>
          Done
        </Button>
      </>
    )}
          <Button 
            variant="outlined" 
            startIcon={!isMobile ? <UploadFileIcon /> : null}
            onClick={() => setUploadOpen(true)}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            {isMobile ? 'Upload Spreadsheet' : 'Upload via Spreadsheet'}
          </Button>
          <Button variant="contained" onClick={handleAdd} fullWidth={isMobile} size={isMobile ? 'medium' : 'medium'}>Add Ski</Button>
        </Box>
      </Box>
      <Box sx={{ mb: 2, mt: isMobile ? 2 : 0 }}>
        <Typography variant="h6">Classic Skis</Typography>
      </Box>
      {loading ? <CircularProgress /> : (
        classicSkis.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center', mb: 4 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No classic skis yet
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Add your first pair of classic skis to get started.
            </Typography>
            <Button
              variant="contained"
              onClick={handleAdd}
            >
              Add Your First Classic Ski
            </Button>
          </Paper>
        ) : (
          <SkisTable skis={classicSkis} onEdit={handleEdit} onDelete={handleDelete} onDetail={handleDetail} selectedIds={selectedIds} onToggleSelect={toggleSelect} showCheckBox={showQRCheckBoxes} setSkis={setSkis} />
        )
      )}
      <Box sx={{ mt: 4, mb: 2 }}>
        <Typography variant="h6">Skate Skis</Typography>
      </Box>
      {loading ? <CircularProgress /> : (
        skateSkis.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center', mb: 4 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No skate skis yet
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Add your first pair of skate skis to get started.
            </Typography>
            <Button
              variant="contained"
              onClick={handleAdd}
            >
              Add Your First Skate Ski
            </Button>
          </Paper>
        ) : (
          <SkisTable skis={skateSkis} onEdit={handleEdit} onDelete={handleDelete} onDetail={handleDetail} selectedIds={selectedIds} onToggleSelect={toggleSelect} showCheckBox={showQRCheckBoxes} setSkis={setSkis} />
        )
      )}
      <SkiForm open={modal.open} onClose={() => setModal({ open: false, ski: null })} onSave={handleSave} initial={modal.ski} defaultTechnique={user?.default_technique} />
      <SkiDetailModal open={detail.open} onClose={() => setDetail({ open: false, ski: null })} ski={detail.ski} skis={skis} setSkis={setSkis} />
      <SkiSpreadsheetUpload 
        open={uploadOpen} 
        onClose={() => setUploadOpen(false)} 
        onSuccess={() => {
          fetchSkis();
          setUploadOpen(false);
        }}
      />
    </Box>
  );
} 